import React from 'react';
import styled from 'libs/styled';
import BlockGridWrapper from 'components/wrappers/BlockGridWrapper';
import type { ContentSliderStaticGridProps, GridLayoutMap } from './types';

/** Above this item count, use ContentSliderDesktop/ContentSliderMobile instead of static grid. */
export const MAX_ITEMS_STATIC_GRID = 4;

const GRID_SPANS_3: Record<number, string> = {
    0: '1 / 5',
    1: '13 / 21',
    2: '21 / 25',
};

const GRID_SPANS_4: Record<number, string> = {
    0: '1 / 5',
    1: '5 / 9',
    2: '13 / 21',
    3: '21 / 25',
};

/** Default layout matching the slider; use when gridLayoutByCount is not passed. */
const DEFAULT_GRID_LAYOUT_BY_COUNT: Record<number, GridLayoutMap> = {
    3: GRID_SPANS_3,
    4: GRID_SPANS_4,
};

const filterTransientProps = { shouldForwardProp: (prop: string) => !prop.startsWith('$') };

const ItemSlot = styled('div', filterTransientProps)<{ $gridColumn: string }>`
    grid-column: ${({ $gridColumn }) => $gridColumn};
    min-width: 0;
`;

/**
 * Fixed grid of content items. Use when item count is small (e.g. ≤MAX_ITEMS_STATIC_GRID);
 * for more items use ContentSliderDesktop or ContentSliderMobile.
 * Benefits: all items visible at once (no interaction), simpler than a slider, and better
 * for accessibility and perceived performance. Compose with SliderHeader at the call site.
 */
const ContentSliderStaticGrid = <T,>({
    items,
    getItemKey,
    renderItem,
    gridLayoutByCount = DEFAULT_GRID_LAYOUT_BY_COUNT,
}: ContentSliderStaticGridProps<T>) => {
    if (items.length === 0) {
        return null;
    }

    const layoutMap = gridLayoutByCount[items.length] ?? gridLayoutByCount[MAX_ITEMS_STATIC_GRID] ?? {};
    const defaultSpan = '1 / 5';

    return (
        <BlockGridWrapper>
            {items.map((item, index) => (
                <ItemSlot key={getItemKey(item)} $gridColumn={layoutMap?.[index] ?? defaultSpan}>
                    {renderItem(item, index)}
                </ItemSlot>
            ))}
        </BlockGridWrapper>
    );
};

export default ContentSliderStaticGrid;
export type { ContentSliderStaticGridProps };
